import React from "react";

interface PaginationComponentProps {
    totalPages: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    onlyShow?: number; // Tambahan opsi onlyShow
}

const PaginationComponent: React.FC<PaginationComponentProps> = ({
    totalPages,
    currentPage,
    onPageChange,
}) => {
    const handleClick = (pageNumber: number) => {
        if (pageNumber !== currentPage) {
            onPageChange(pageNumber);
        }
    };

    const getPageNumbers = (): (number | string)[] => {
        const siblingCount = 1;
        const totalPageNumbers = siblingCount + 5; // 1 (First) + 1 (Last) + 1 (Current) + 2 * Sibling + 2 * Ellipses = 7

        if (totalPages <= totalPageNumbers) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

        const showLeftDots = leftSiblingIndex > 2;
        const showRightDots = rightSiblingIndex < totalPages - 1;

        if (!showLeftDots && showRightDots) {
            const leftItemCount = 3 + 2 * siblingCount;
            const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
            return [...leftRange, "...", totalPages];
        }

        if (showLeftDots && !showRightDots) {
            const rightItemCount = 3 + 2 * siblingCount;
            const rightRange = Array.from(
                { length: rightItemCount },
                (_, i) => totalPages - rightItemCount + 1 + i
            );
            return [1, "...", ...rightRange];
        }

        if (showLeftDots && showRightDots) {
            const middleRange = Array.from(
                { length: rightSiblingIndex - leftSiblingIndex + 1 },
                (_, i) => leftSiblingIndex + i
            );
            return [1, "...", ...middleRange, "...", totalPages];
        }

        return [];
    };

    return (
        <nav className='mt-3'>
            <ul className="pagination d-flex justify-content-center gap-2 flex-wrap">
                <li className="page-item">
                    <button
                        className={`page-link ${currentPage === 1 ? 'disabled' : ''}`}
                        onClick={() => handleClick(1)}
                        disabled={currentPage === 1}
                    >First</button>
                </li>

                <li className="page-item">
                    <button
                        className={`page-link ${currentPage === 1 ? 'disabled' : ''}`}
                        onClick={() => handleClick(currentPage - 1)}
                        disabled={currentPage === 1}
                    >Previous</button>
                </li>

                {getPageNumbers().map((pageNumber, index) => {
                    if (pageNumber === "...") {
                        return (
                            <li key={`ellipsis-${index}`} className="page-item disabled">
                                <span className="page-link">...</span>
                            </li>
                        );
                    }

                    const num = pageNumber as number;
                    return (
                        <li key={num} className={`page-item ${currentPage === num ? 'active' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handleClick(num)}
                            >{num}</button>
                        </li>
                    );
                })}

                <li className="page-item">
                    <button
                        className={`page-link ${currentPage === totalPages ? 'disabled' : ''}`}
                        onClick={() => handleClick(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >Next</button>
                </li>

                <li className="page-item">
                    <button
                        className={`page-link ${currentPage === totalPages ? 'disabled' : ''}`}
                        onClick={() => handleClick(totalPages)}
                        disabled={currentPage === totalPages}
                    >Last</button>
                </li>
            </ul>
        </nav>
    );
};

export default PaginationComponent;
